import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, EMPTY, finalize, fromEvent, Observable, of, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';

import { MessageService } from 'primeng/api';
import { Popover } from 'primeng/popover';

import { NotificationApiService } from '../notification-api.service';
import { OverflowClampDirective } from './overflow-clamp.directive';
import { UserNotificationV1 } from '../../../api/models';
import { AuditingService } from '../../../api/services/auditing.service';
import { ReportingApiService } from '../../../reporting/reporting-api.service';

export type NotificationTab = 'new' | 'archived';

type NotificationActionType = NonNullable<UserNotificationV1['actionType']>;
// No real-time requirement: 60s keeps the badge reasonably fresh while polling only when visible.
const NOTIFICATION_POLL_INTERVAL_MS = 60 * 1000;

const ACTION_LABELS: Record<NotificationActionType, string> = {
  review_request: 'Review request',
  view_request: 'View request',
  complete_request: 'Complete request',
  download_audit_export: 'Download',
  download_requests_export: 'Download',
};

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, Popover, OverflowClampDirective],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBellComponent implements OnInit {
  private readonly api = inject(NotificationApiService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly auditingService = inject(AuditingService);
  private readonly reportingApi = inject(ReportingApiService);
  private readonly document = inject(DOCUMENT);

  @ViewChild('panel') panelRef?: Popover;

  readonly notifications = signal<UserNotificationV1[]>([]);
  readonly archivedNotifications = signal<UserNotificationV1[]>([]);
  readonly activeTab = signal<NotificationTab>('new');
  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly panelOpen = signal(false);

  readonly unreadCount = computed(() => this.notifications().filter((n) => !n.read).length);
  readonly visibleNotifications = computed(() =>
    this.activeTab() === 'new' ? this.notifications() : this.archivedNotifications()
  );

  /** Ids of cards whose message is expanded to full length, and ids whose clamped message overflows. */
  private readonly expandedIds = signal<ReadonlySet<string>>(new Set());
  private readonly overflowingIds = signal<ReadonlySet<string>>(new Set());

  ngOnInit(): void {
    this.refreshNew();

    // Poll only while the tab is visible; refetch immediately on return, pause while hidden.
    fromEvent(this.document, 'visibilitychange')
      .pipe(
        map(() => this.document.visibilityState === 'visible'),
        startWith(this.document.visibilityState === 'visible'),
        distinctUntilChanged(),
        switchMap((visible, index) => {
          if (!visible) return EMPTY;
          // Initial visible spell waits a full interval (load already fetched); on return, refetch now.
          const initialDelay = index === 0 ? NOTIFICATION_POLL_INTERVAL_MS : 0;
          return timer(initialDelay, NOTIFICATION_POLL_INTERVAL_MS);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.refreshNew());
  }

  onPanelShow(): void {
    this.panelOpen.set(true);
    this.activeTab.set('new');
    this.refreshNew();
  }

  onPanelHide(): void {
    this.panelOpen.set(false);
    this.expandedIds.set(new Set());
  }

  /** Expand/collapse a card's full message. */
  toggleExpanded(id: string | null | undefined): void {
    if (!id) return;
    this.expandedIds.update((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  isExpanded(id: string | null | undefined): boolean {
    return !!id && this.expandedIds().has(id);
  }

  /** Called by the clamp directive once the card is laid out; records whether its message is cut off. */
  setOverflowing(id: string | null | undefined, overflowing: boolean): void {
    if (!id) return;
    this.overflowingIds.update((set) => {
      if (set.has(id) === overflowing) return set;
      const next = new Set(set);
      if (overflowing) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  /** Show the toggle when the message is clipped, or while it is expanded (so "Show less" stays). */
  showToggle(id: string | null | undefined): boolean {
    return this.isExpanded(id) || (!!id && this.overflowingIds().has(id));
  }

  closePanel(): void {
    this.panelRef?.hide();
  }

  onTabChanged(tab: NotificationTab): void {
    if (this.activeTab() === tab) return;
    this.activeTab.set(tab);
    if (tab === 'archived') this.refreshArchived();
    else this.refreshNew();
  }

  refreshNew(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.api
      .listNotifications(false)
      .pipe(
        catchError(() => {
          this.loadError.set(true);
          return of([] as UserNotificationV1[]);
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((items) => this.notifications.set(items ?? []));
  }

  refreshArchived(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.api
      .listNotifications(true)
      .pipe(
        catchError(() => {
          this.loadError.set(true);
          return of([] as UserNotificationV1[]);
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((items) => this.archivedNotifications.set(items ?? []));
  }

  retry(): void {
    if (this.activeTab() === 'archived') this.refreshArchived();
    else this.refreshNew();
  }

  onCardClicked(notification: UserNotificationV1): void {
    this.markReadLocally(notification);
  }

  onArchive(notification: UserNotificationV1, event: Event): void {
    event.stopPropagation();
    if (!notification.id) return;
    this.api.archive(notification.id).subscribe({
      next: () => this.notifications.update((items) => items.filter((n) => n.id !== notification.id)),
      error: () =>
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'The notification could not be archived. Please try again.',
        }),
    });
  }

  onUnarchive(notification: UserNotificationV1, event: Event): void {
    event.stopPropagation();
    if (!notification.id) return;
    this.api.unarchive(notification.id).subscribe({
      next: () => this.archivedNotifications.update((items) => items.filter((n) => n.id !== notification.id)),
      error: () =>
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'The notification could not be restored. Please try again.',
        }),
    });
  }

  onAction(notification: UserNotificationV1, event: Event): void {
    event.stopPropagation();
    this.markReadLocally(notification);

    if (notification.actionType === 'download_audit_export') {
      this.downloadAuditExport(notification);
      return;
    }

    if (notification.actionType === 'download_requests_export') {
      this.downloadRequestsExport(notification);
      return;
    }

    if (notification.actionId) {
      void this.router.navigate(['/requests', notification.actionId]);
    }

    this.closePanel();
  }

  private downloadAuditExport(notification: UserNotificationV1): void {
    if (!notification.actionId) return;
    this.triggerExportDownload(
      this.auditingService.getExportStatus({ jobId: notification.actionId }),
      'The audit export is not ready yet. Please try again shortly.'
    );
  }

  private downloadRequestsExport(notification: UserNotificationV1): void {
    if (!notification.actionId) return;
    this.triggerExportDownload(
      this.reportingApi.getApprovedRequestsExportStatus(notification.actionId),
      'The export is not ready yet. Please try again shortly.'
    );
  }

  private triggerExportDownload(
    fetch$: Observable<{ status?: string; downloadUrl?: string | null }>,
    notReadyDetail: string
  ): void {
    fetch$.subscribe({
      next: (job: { status?: string; downloadUrl?: string | null }) => {
        if (job.status !== 'COMPLETED' || !job.downloadUrl) {
          this.messageService.add({ severity: 'info', summary: 'Export not ready', detail: notReadyDetail });
          return;
        }

        const parsed = new URL(job.downloadUrl);
        if (parsed.protocol !== 'https:') {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Invalid download URL.' });
          return;
        }

        const a = document.createElement('a');
        a.href = parsed.href;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        a.remove();

        this.closePanel();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'The export could not be downloaded. Please try again.',
        });
      },
    });
  }

  actionLabel(n: UserNotificationV1): string | null {
    return n.actionType ? ACTION_LABELS[n.actionType] ?? null : null;
  }

  badgeClass(n: UserNotificationV1): string {
    return `card__badge card__badge--${n.badgeType ?? 'pending'}`;
  }

  /** Published date only (no time), e.g. "17/06/2026" — a single timestamp per the design review (#48894). */
  publishedLabel(iso: string | null | undefined): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  private markReadLocally(notification: UserNotificationV1): void {
    if (notification.read || notification.archived || !notification.id) return;
    this.notifications.update((items) => items.map((n) => (n.id === notification.id ? { ...n, read: true } : n)));
    this.api
      .markAsRead(notification.id)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }
}
